import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";

const ALLOWED_DISCORD_IDS = ["972599697229365278", "1351799105189318806"];

function getDiscordId(user) {
  return (
    user?.identities?.find((i) => i.provider === "discord")?.identity_data
      ?.sub ||
    user?.identities?.find((i) => i.provider === "discord")?.id ||
    user?.user_metadata?.provider_id ||
    user?.user_metadata?.sub ||
    null
  );
}

function saveDiscordUserToLocalStorage(user, source = "unknown") {
  if (!user) return;

  const discordId = getDiscordId(user);

  if (!discordId) {
    console.warn(`[Login] No Discord ID found from ${source}:`, user);
    return;
  }

  const discordUser = {
    discord_id: discordId,
    username:
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.user_metadata?.preferred_username ||
      "Discord User",
    avatar: user?.user_metadata?.avatar_url || null,
    email: user?.email || null,
  };

  localStorage.setItem("outfall_discord_user", JSON.stringify(discordUser));

  console.log(`[Login] Saved Discord user from ${source}:`, discordUser);
}

export default function Login() {
  const [searchParams] = useSearchParams();

  const [error, setError] = useState(
    searchParams.get("error") === "unauthorized"
      ? "Your Discord account is not authorised to access this area."
      : "",
  );

  const [loading, setLoading] = useState(false);

  const rawFrom = searchParams.get("from") || "";

  const returnPath =
    rawFrom && rawFrom.startsWith("/") && rawFrom !== "/login"
      ? rawFrom
      : "/home";

  useEffect(() => {
    async function loadCurrentSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      saveDiscordUserToLocalStorage(session?.user, "current session");
    }

    loadCurrentSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[Login] Auth state changed:", event);
      saveDiscordUserToLocalStorage(session?.user, "auth state change");
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleDiscordLogin() {
    setError("");
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: {
        redirectTo: window.location.origin + returnPath,
      },
    });

    if (authError) {
      setError(authError.message);
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="flex min-h-screen items-center justify-center px-6 pb-20 pt-24">
        <div className="w-full max-w-md">
          <p className="font-black uppercase text-[#ffb56f]">Admin</p>

          <h1 className="outfall-heading text-[clamp(48px,8vw,90px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
            Sign In
          </h1>

          <div className="mt-10 overflow-hidden rounded-[24px] border-4 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.35)]">
            {error && (
              <p className="mb-6 rounded-xl bg-red-500/20 px-4 py-3 text-sm font-bold text-red-400">
                {error}
              </p>
            )}

            <p className="mb-8 text-sm font-bold text-white/50">
              This area is restricted to authorised staff only.
            </p>

            <button
              onClick={handleDiscordLogin}
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-[#5865F2] px-6 py-4 font-black text-white transition hover:bg-[#4752C4] disabled:opacity-50"
            >
              <svg
                width="24"
                height="18"
                viewBox="0 0 24 18"
                fill="currentColor"
              >
                <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z" />
              </svg>

              {loading ? "Redirecting…" : "Continue with Discord"}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export { ALLOWED_DISCORD_IDS };
